/**
 * Simulation Store
 *
 * Zustand store for managing EnergyPlus simulation state.
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import type { ErrorSummary, ESOData, OutputVariable, TimeSeries } from './parsers/types';
import type { ConsoleLine, ProgressUpdate, SimulationPhase, TimeEstimate } from './progress';
import { ConsoleBuffer, formatDuration, ProgressTracker } from './progress';
import { useOutputLookupStore } from '../stores/outputLookupStore';
import { saveSimulationToHistory } from './simulationHistoryCache';

/**
 * Worker message types for WASM loading
 */
interface WasmReadyMessage {
  type: 'wasm-ready';
  mockMode: boolean;
}

interface WasmErrorMessage {
  type: 'wasm-error';
  error: string;
}

type WasmWorkerMessage = WasmReadyMessage | WasmErrorMessage;

/**
 * Worker message types for simulation
 */
interface SimProgressMessage {
  type: 'progress';
  update: ProgressUpdate;
}

interface SimConsoleMessage {
  type: 'console';
  text: string;
}

interface SimCompleteMessage {
  type: 'complete';
  output: SimulationOutput;
}

interface SimErrorMessage {
  type: 'error';
  error: string;
}

type SimulationWorkerMessage = SimProgressMessage | SimConsoleMessage | SimCompleteMessage | SimErrorMessage;

/**
 * Simulation status
 */
export type SimulationStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'running'
  | 'complete'
  | 'error'
  | 'cancelled';

/**
 * Simulation input files
 */
export interface SimulationInput {
  idf: string;
  idfName: string;
  epw?: string;
  epwName?: string;
}

/**
 * Simulation output files
 */
export interface SimulationOutput {
  eso?: ESOData;
  mtr?: ESOData;
  err?: ErrorSummary;
  sql?: ArrayBuffer;
  html?: string;
  csv?: string;
  /** Raw RDD file content for output variable dictionary */
  rdd?: string;
  /** Raw MDD file content for meter dictionary */
  mdd?: string;
}

/**
 * Simulation state
 */
export interface SimulationState {
  // Status
  status: SimulationStatus;
  phase: SimulationPhase;
  error: string | null;

  // Progress
  progress: number;
  progressMessage: string;
  timeEstimate: TimeEstimate;

  // Console
  consoleLines: ConsoleLine[];
  autoScroll: boolean;

  // Input
  input: SimulationInput | null;

  // Output
  output: SimulationOutput | null;
  availableVariables: OutputVariable[];

  // WASM status
  wasmLoaded: boolean;
  wasmLoading: boolean;
  wasmError: string | null;
  mockMode: boolean;

  // Internal trackers (not serialized)
  _progressTracker: ProgressTracker;
  _consoleBuffer: ConsoleBuffer;
}

/**
 * Simulation actions
 */
export interface SimulationActions {
  // WASM management
  loadWasm: () => Promise<void>;

  // Simulation control
  setInput: (input: SimulationInput) => void;
  startSimulation: () => Promise<void>;
  cancelSimulation: () => void;
  forceCancel: () => void;
  reset: () => void;

  // Progress updates (called from worker)
  updateProgress: (update: ProgressUpdate) => void;
  addConsoleLine: (line: string) => void;
  addConsoleLines: (text: string) => void;
  updateTimeEstimate: (estimate: TimeEstimate) => void;

  // Completion handlers
  setComplete: (output: SimulationOutput) => void;
  setError: (error: string) => void;
  setCancelled: () => void;

  // Console controls
  toggleAutoScroll: () => void;
  clearConsole: () => void;
  copyConsole: () => Promise<void>;

  // Results access
  getTimeSeries: (variableId: number) => TimeSeries | undefined;
}

/**
 * Initial state
 */
const initialState: Omit<SimulationState, '_progressTracker' | '_consoleBuffer'> = {
  status: 'idle',
  phase: 'initializing',
  error: null,
  progress: 0,
  progressMessage: '',
  timeEstimate: { elapsed: 0, remaining: null, total: null },
  consoleLines: [],
  autoScroll: true,
  input: null,
  output: null,
  availableVariables: [],
  wasmLoaded: false,
  wasmLoading: false,
  wasmError: null,
  mockMode: false,
};

/**
 * Worker instance (singleton)
 */
let simulationWorker: Worker | null = null;
let workerCreationError: string | null = null;

/**
 * Lock to prevent concurrent WASM loading
 */
let wasmLoadingLock = false;

/**
 * Create a new simulation worker
 */
function createWorker(): Worker {
  try {
    return new Worker(
      new URL('./worker/simulationWorker.ts', import.meta.url),
      { type: 'module' }
    );
  } catch (e) {
    workerCreationError = `Failed to create worker: ${e instanceof Error ? e.message : String(e)}`;
    throw new Error(workerCreationError);
  }
}

/**
 * Get or create simulation worker
 */
function getWorker(): Worker {
  if (workerCreationError) {
    throw new Error(workerCreationError);
  }
  if (!simulationWorker) {
    simulationWorker = createWorker();
  }
  return simulationWorker;
}

/**
 * Terminate and recreate the worker
 * Used to forcefully stop a hung simulation
 */
function terminateAndRecreateWorker(): void {
  if (simulationWorker) {
    simulationWorker.terminate();
    simulationWorker = null;
  }
  // Don't create a new worker here - it will be created on next use
}

/**
 * JSON replacer function to handle Map objects in ESOData
 */
function mapReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return {
      __type: 'Map',
      entries: Array.from(value.entries()),
    };
  }
  return value;
}

/**
 * JSON reviver function to restore Map objects from serialized data
 */
function mapReviver(_key: string, value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    (value as Record<string, unknown>)['__type'] === 'Map' &&
    Array.isArray((value as Record<string, unknown>)['entries'])
  ) {
    const entries = (value as { entries: [unknown, unknown][] })['entries'];
    return new Map(entries);
  }
  return value;
}

/**
 * Create simulation store
 */
export const useSimulationStore = create<SimulationState & SimulationActions>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        ...initialState,
        _progressTracker: new ProgressTracker(),
        _consoleBuffer: new ConsoleBuffer(10000),

      // WASM management
      loadWasm: async () => {
        const state = get();
        if (state.wasmLoaded || state.wasmLoading) return;

        // Use module-level lock to prevent concurrent WASM loading
        // This handles the race where two calls pass the above check before either sets wasmLoading
        if (wasmLoadingLock) return;
        wasmLoadingLock = true;

        set({ wasmLoading: true, wasmError: null, status: 'loading' });

        try {
          const worker = getWorker();

          const mockMode = await new Promise<boolean>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('WASM loading timeout (60s)'));
            }, 60000);

            // Use addEventListener instead of onmessage to avoid overwriting handlers
            const handleMessage = (event: MessageEvent<WasmWorkerMessage | { type: 'console'; text: string }>) => {
              const message = event.data;

              // Handle console messages during loading
              if (message.type === 'console') {
                get().addConsoleLines(message.text);
                return; // Don't clear timeout for console messages
              }

              // Clean up listener once we get a terminal message
              clearTimeout(timeout);
              worker.removeEventListener('message', handleMessage);

              if (message.type === 'wasm-ready') {
                resolve(message.mockMode);
              } else if (message.type === 'wasm-error') {
                reject(new Error(message.error));
              }
            };

            const handleError = (error: ErrorEvent) => {
              clearTimeout(timeout);
              worker.removeEventListener('message', handleMessage);
              worker.removeEventListener('error', handleError);
              const errorMsg = error.message || 'Unknown worker error';
              get().addConsoleLines(`Worker error: ${errorMsg}`);
              reject(new Error(errorMsg));
            };

            worker.addEventListener('message', handleMessage);
            worker.addEventListener('error', handleError);

            worker.postMessage({ type: 'load-wasm' });
          });

          set({ wasmLoaded: true, wasmLoading: false, status: 'ready', mockMode });
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error);
          get().addConsoleLines(`WASM loading failed: ${errorMsg}`);
          set({
            wasmLoading: false,
            wasmError: errorMsg,
            status: 'error',
          });
        } finally {
          wasmLoadingLock = false;
        }
      },

      // Set input files
      setInput: (input) => {
        set({
          input,
          status: get().wasmLoaded ? 'ready' : 'idle',
          output: null,
          error: null,
        });
      },

      // Start simulation
      startSimulation: async () => {
        const state = get();
        if (!state.input) {
          set({ error: 'No input file provided' });
          return;
        }

        if (!state.wasmLoaded) {
          await get().loadWasm();
          if (!get().wasmLoaded) return;
        }

        // Reset progress trackers
        state._progressTracker.reset();
        state._consoleBuffer.clear();

        set({
          status: 'running',
          phase: 'initializing',
          progress: 0,
          progressMessage: 'Starting simulation...',
          consoleLines: [],
          error: null,
          output: null,
          timeEstimate: { elapsed: 0, remaining: null, total: null },
        });

        const worker = getWorker();

        // Set up message handler using addEventListener to avoid overwriting other handlers
        const handleSimMessage = (event: MessageEvent<SimulationWorkerMessage>) => {
          const message = event.data;

          switch (message.type) {
            case 'progress':
              get().updateProgress(message.update);
              break;
            case 'console':
              get().addConsoleLines(message.text);
              break;
            case 'complete':
              // Clean up listeners on completion
              worker.removeEventListener('message', handleSimMessage);
              worker.removeEventListener('error', handleSimError);
              get().setComplete(message.output);
              break;
            case 'error':
              // Clean up listeners on error
              worker.removeEventListener('message', handleSimMessage);
              worker.removeEventListener('error', handleSimError);
              get().setError(message.error);
              break;
          }
        };

        const handleSimError = (error: ErrorEvent) => {
          worker.removeEventListener('message', handleSimMessage);
          worker.removeEventListener('error', handleSimError);
          const errorMsg = error.message || 'Unknown worker error';
          get().setError(`Worker error: ${errorMsg}`);
        };

        worker.addEventListener('message', handleSimMessage);
        worker.addEventListener('error', handleSimError);

        // Send simulation request
        worker.postMessage({
          type: 'simulate',
          idf: state.input.idf,
          epw: state.input.epw,
        });
      },

      // Cancel simulation (soft cancel - may not work if simulation is hung)
      cancelSimulation: () => {
        const worker = getWorker();
        worker.postMessage({ type: 'cancel' });
        set({ status: 'cancelled', progressMessage: 'Simulation cancelled' });
      },

      // Force cancel simulation by terminating the worker
      // Use this when the simulation appears hung and soft cancel doesn't work
      forceCancel: () => {
        get().addConsoleLines('Force cancelling simulation - terminating worker...');
        terminateAndRecreateWorker();
        set({
          status: 'cancelled',
          progressMessage: 'Simulation force cancelled (worker terminated)',
          wasmLoaded: false,
          wasmLoading: false,
          mockMode: false,
        });
        get().addConsoleLines('Worker terminated. WASM will need to be reloaded for next simulation.');
      },

      // Reset state
      reset: () => {
        set({
          ...initialState,
          _progressTracker: new ProgressTracker(),
          _consoleBuffer: new ConsoleBuffer(10000),
          wasmLoaded: get().wasmLoaded,
          mockMode: get().mockMode,
        });
      },

      // Update progress
      updateProgress: (update) => {
        set({
          phase: update.phase,
          progress: update.progress,
          progressMessage: update.message,
        });
      },

      // Add single console line
      addConsoleLine: (line) => {
        const state = get();
        const consoleLine = state._progressTracker.getConsoleLine(line);
        state._consoleBuffer.addLine(consoleLine);

        // Parse for progress
        state._progressTracker.parseLine(line);

        set({
          consoleLines: state._consoleBuffer.getLines(),
          ...state._progressTracker.getCurrentProgress(),
        });
      },

      // Add multiple console lines
      addConsoleLines: (text) => {
        const state = get();
        const lines = text.split('\n').filter((l) => l.trim());

        for (const line of lines) {
          const consoleLine = state._progressTracker.getConsoleLine(line);
          state._consoleBuffer.addLine(consoleLine);
          state._progressTracker.parseLine(line);
        }

        const currentProgress = state._progressTracker.getCurrentProgress();
        const timeEstimate = state._progressTracker.getTimeEstimate();

        set({
          consoleLines: state._consoleBuffer.getLines(),
          phase: currentProgress.phase,
          progress: currentProgress.progress,
          progressMessage: currentProgress.message,
          timeEstimate,
        });
      },

      // Update time estimate
      updateTimeEstimate: (estimate) => {
        set({ timeEstimate: estimate });
      },

      // Set complete
      setComplete: (output) => {
        const state = get();
        const availableVariables: OutputVariable[] = [];

        if (output.eso) {
          for (const variable of output.eso.variables.values()) {
            availableVariables.push(variable);
          }
        }

        // Load RDD/MDD data into the output lookup store for autocomplete
        const outputLookupStore = useOutputLookupStore.getState();
        if (output.rdd) {
          outputLookupStore.loadRDD(output.rdd);
        }
        if (output.mdd) {
          outputLookupStore.loadMDD(output.mdd);
        }

        set({
          status: 'complete',
          phase: 'complete',
          progress: 100,
          progressMessage: 'Simulation completed successfully',
          output,
          availableVariables,
        });

        // Save to simulation history (async, fire-and-forget)
        if (state.input) {
          saveSimulationToHistory(
            state.input,
            output,
            availableVariables,
            state.timeEstimate.elapsed
          ).catch((err) => {
            console.error('Failed to save simulation to history:', err);
          });
        }
      },

      // Set error
      setError: (error) => {
        const state = get();
        set({
          status: 'error',
          phase: 'error',
          error,
          progressMessage: `Error: ${error}`,
        });

        // Save failed simulation to history (async, fire-and-forget)
        if (state.input) {
          saveSimulationToHistory(
            state.input,
            null,
            [],
            state.timeEstimate.elapsed,
            error
          ).catch((err) => {
            console.error('Failed to save simulation error to history:', err);
          });
        }
      },

      // Set cancelled
      setCancelled: () => {
        set({
          status: 'cancelled',
          progressMessage: 'Simulation cancelled by user',
        });
      },

      // Toggle auto-scroll
      toggleAutoScroll: () => {
        set({ autoScroll: !get().autoScroll });
      },

      // Clear console
      clearConsole: () => {
        get()._consoleBuffer.clear();
        set({ consoleLines: [] });
      },

      // Copy console to clipboard
      copyConsole: async () => {
        const text = get()._consoleBuffer.toString();
        await navigator.clipboard.writeText(text);
      },

      // Get time series for a variable
      getTimeSeries: (variableId) => {
        const output = get().output;
        if (!output?.eso) return undefined;
        return output.eso.timeSeries.get(variableId);
      },
    }),
      {
        name: 'simulation-results',
        storage: createJSONStorage(() => sessionStorage, {
          replacer: mapReplacer,
          reviver: mapReviver,
        }),
        // Only persist the essential results data, not runtime state
        partialize: (state) => ({
          status: state.status,
          output: state.output,
          availableVariables: state.availableVariables,
        }),
        // Handle rehydration - merge persisted state with initial state
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<SimulationState> | undefined;
          return {
            ...currentState,
            status: persisted?.status ?? currentState.status,
            output: persisted?.output ?? currentState.output,
            availableVariables: persisted?.availableVariables ?? currentState.availableVariables,
          };
        },
        // Debug hydration
        onRehydrateStorage: () => {
          return (_state, error) => {
            if (error) {
              console.error('[SimulationStore] Hydration error:', error);
            }
          };
        },
      }
    ),
    { name: 'simulation-store' }
  )
);

/**
 * Format progress for display
 */
export function formatProgress(state: SimulationState): string {
  const { progress, phase, timeEstimate } = state;

  let phaseLabel = '';
  switch (phase) {
    case 'initializing':
      phaseLabel = 'Initializing';
      break;
    case 'warmup':
      phaseLabel = 'Warmup';
      break;
    case 'sizing':
      phaseLabel = 'Sizing';
      break;
    case 'simulation':
      phaseLabel = 'Running';
      break;
    case 'postprocess':
      phaseLabel = 'Post-processing';
      break;
    case 'complete':
      phaseLabel = 'Complete';
      break;
    case 'error':
      phaseLabel = 'Error';
      break;
  }

  let timeStr = '';
  if (timeEstimate.elapsed > 0) {
    timeStr = ` • Elapsed: ${formatDuration(timeEstimate.elapsed)}`;
    if (timeEstimate.remaining !== null) {
      timeStr += ` • Remaining: ~${formatDuration(timeEstimate.remaining)}`;
    }
  }

  return `${phaseLabel}: ${progress.toFixed(0)}%${timeStr}`;
}

/**
 * Selectors
 */
export const selectIsRunning = (state: SimulationState) => state.status === 'running';
export const selectCanRun = (state: SimulationState) =>
  state.input !== null && (state.status === 'idle' || state.status === 'ready' || state.status === 'complete' || state.status === 'error');
export const selectHasResults = (state: SimulationState) => state.output !== null;
export const selectIsMockMode = (state: SimulationState) => state.mockMode;
